import React from 'react'
import {connect} from 'react-redux'
import propTypes from 'prop-types'
import PivotTable from 'containers/pivotTable'
import FinancialString from 'containers/financialString'
import {getPartType} from 'helpers/parts'
import {get, isNil} from 'lodash'
import {prefix} from "helpers/component"
import {getPercentageString, formatRoundedNumber} from 'helpers/financial'
import QuoteHelper, {
  getNetMarginWhenDiscountIsApplied,
  getNetMarkupWhenDiscountIsApplied,
  getNominalCode,
  ReferralType,
} from 'helpers/quotes'
import * as FieldsHelper from 'helpers/fields'
import {getPricingAccuracy, getRegionalLocaleTaxTerm} from 'selectors/settings'
import {getPackTypes} from 'selectors/priceLists'
import {getCurrencies, getNominalCodes} from 'selectors/data'
import {filterToSalesNominalCodes, getNominalCodeOptions} from 'helpers/nominalCodes'
import {getUser} from 'selectors/auth'
import {applyColumnSizes, applyColumnVisibility} from 'helpers/pivotTable'
import {maxLength} from 'helpers/string'
import {
  getAllItems,
  getSupplierCompanyNameForOrderItem,
  getToOrderQuantityForOrderItem,
  getTotalQuantityForItem,
} from 'helpers/orders'
import {userHasPermission} from 'helpers/user'
import {permissions} from 'helpers/constants'
import {getSelectedOrderItems} from 'selectors/quotes'
import PartImageColumn from "components/partImageColumn"
import iconNames from 'theme/icons'
import themeVars from 'theme/themeVars'

const singular = 'Quote Item'
const plural = 'Quote Items'
const icon = iconNames.orderItem

const rowStyle = e => {
  if (get(e, 'dormant')) {
    return {
      color: themeVars.palette.disabledTextColor,
      textDecoration: 'line-through',
    }
  }
}

const getClass = prefix('quoteItemPivotTable-')

export const getColumnsDefinition = (props) => {
  const {
    user,
    currencyId,
    pricingAccuracy,
    userFields,
    nominalCodes,
    regionalLocaleTaxTerm,
    maxDescLength,
    allItems,
    order,
  } = props

  const nominalCodeOptions =
      getNominalCodeOptions(filterToSalesNominalCodes(nominalCodes, user.settings.accountsIntegration))
  const canViewCosts = userHasPermission(user, permissions.VIEW_QUOTE_COSTS)

  const ret = {
    letter: {
      key: 'letter',
      label: 'Item',
      alias: 'Item',
      size: 50,
      visible: true,
      style: rowStyle,
      data: e => get(e, 'lineItemLetter'),
      order: 0,
    },
    partImage: {
      key: 'partImage',
      label: 'Image',
      size: 20,
      visible: false,
      editable: false,
      style: () => ({
        height: 40,
      }),
      data: e => <PartImageColumn getClass={getClass} entity={e}/>,
      order: 1,
    },
    partCode: {
      key: 'partCode',
      label: 'Part Code',
      size: 60,
      visible: true,
      style: rowStyle,
      data: e => get(e, 'partCode'),
      order: 1,
    },
    desc: {
      key: 'desc',
      label: 'Description',
      size: 350,
      visible: true,
      style: rowStyle,
      data: e => {
        const desc = get(e, 'desc')
        return maxDescLength ? maxLength(desc, maxDescLength) : desc
      },
      order: 2,
    },
    quantity: {
      key: 'quantity',
      label: 'Quantity',
      size: 49,
      visible: true,
      align: 'right',
      style: rowStyle,
      data: e => get(e, 'quantity'),
      order: 3,
    },
    totalQuantity: {
      key: 'totalQuantity',
      label: 'Qty Needed',
      size: 69,
      visible: false,
      align: 'right',
      style: rowStyle,
      data: e => getTotalQuantityForItem(e, allItems),
      order: 3,
    },
    packType: {
      key: 'packType',
      label: 'Pack Type',
      size: 75,
      visible: true,
      style: rowStyle,
      data: e => get(e, 'partPackDesc'),
      order: 4,
    },
    markup: {
      key: 'markup',
      label: 'Markup',
      size: 63,
      align: 'right',
      visible: true,
      style: rowStyle,
      data: e => getNetMarkupWhenDiscountIsApplied(e),
      order: 6,
    },
    unitPrice: {
      key: 'unitPrice',
      label: 'Unit Price',
      size: 75,
      align: 'right',
      visible: true,
      style: rowStyle,
      data: e => <FinancialString
          value={get(e, 'unitPrice')}
          currencyId={currencyId}
          precision={pricingAccuracy}/>,
      order: 7,
    },
    totalPrice: {
      key: 'totalPrice',
      label: 'Total Price',
      size: 80,
      align: 'right',
      visible: false,
      style: rowStyle,
      data: e => <FinancialString
          value={get(e, 'totalPrice')}
          currencyId={currencyId}/>,
    },
    discount: {
      key: 'discount',
      label: 'Discount',
      size: 49,
      align: 'right',
      visible: true,
      style: rowStyle,
      data: e => getPercentageString(get(e, 'discount'), 2),
      order: 8,
    },
    netPrice: {
      key: 'netPrice',
      label: 'Net Price',
      size: 95,
      align: 'right',
      visible: true,
      style: rowStyle,
      data: e => <FinancialString
          value={get(e, 'netPrice')}
          currencyId={currencyId}/>,
      order: 9,
    },
    tax: {
      key: 'tax',
      label: regionalLocaleTaxTerm,
      size: 80,
      align: 'right',
      visible: false,
      style: rowStyle,
      data: e => <FinancialString
          value={get(e, 'tax')}
          currencyId={currencyId}/>,
    },
    netUnitPrice: {
      key: 'netUnitPrice',
      label: 'Net Unit Price',
      size: 95,
      align: 'right',
      visible: false,
      style: rowStyle,
      data: e => <FinancialString
          value={get(e, 'netUnitPrice')}
          precision={pricingAccuracy}
          currencyId={currencyId}/>,
    },
    partType: {
      key: 'partType',
      label: 'Part Type',
      size: 75,
      visible: false,
      style: rowStyle,
      data: e => get(getPartType(e.partType), 'label', ''),
    },
    margin: {
      key: 'margin',
      label: 'Margin',
      size: 63,
      align: 'right',
      visible: false,
      data: e => getNetMarginWhenDiscountIsApplied(e),
    },
    profit: {
      key: 'profit',
      label: 'Profit',
      size: 80,
      align: 'right',
      visible: false,
      style: rowStyle,
      data: e => <FinancialString
          value={get(e, 'profit')}
          currencyId={currencyId}/>,
    },
    totalIncTax: {
      key: 'totalIncTax',
      label: 'Price Inc Tax',
      size: 80,
      align: 'right',
      visible: false,
      style: rowStyle,
      data: e => <FinancialString
          value={get(e, 'totalPriceIncTax', null)}
          currencyId={currencyId}/>,
    },
    length: {
      key: 'length',
      label: 'Length',
      size: 49,
      align: 'right',
      visible: false,
      order: 10,
      style: rowStyle,
      data: e => get(e, 'length') || null,
    },
    width: {
      key: 'width',
      label: 'Width',
      size: 49,
      align: 'right',
      visible: false,
      order: 11,
      style: rowStyle,
      data: e => get(e, 'width') || null,
    },
    height: {
      key: 'height',
      label: 'Height',
      size: 49,
      align: 'right',
      visible: false,
      order: 12,
      style: rowStyle,
      data: e => get(e, 'height') || null,
    },
    nominalCode: {
      key: 'nominalCode',
      label: 'Nominal Code',
      size: 75,
      align: 'right',
      visible: false,
      style: rowStyle,
      data: e => {
        const nominalCode = nominalCodeOptions.find(option => option.value === `${getNominalCode(e)}`)
        return !isNil(nominalCode)
            ? get(nominalCode, 'label', null)
            : !isNil(getNominalCode(e))
                ? getNominalCode(e)
                : null
      },
    },
    referalFee: {
      key: 'referalFee',
      label: 'Referral Fee',
      size: 75,
      align: 'right',
      visible: false,
      style: rowStyle,
      data: e => {
        if (!order || order.referralType !== ReferralType.percentage)
          return getPercentageString(formatRoundedNumber(parseFloat(0), 2))
        let value = get(e, 'referralValue')
        if (isNil(value)) value = get(e, 'orderReferralValue')
        if (isNil(value)) value = get(order, 'referralValue')
        if (isNil(value)) value = 0
        return getPercentageString(formatRoundedNumber(parseFloat(value), 2))
      },
    },
    toOrder: {
      key: 'toOrder',
      label: 'To Order',
      size: 75,
      align: 'right',
      visible: false,
      order: 13,
      style: rowStyle,
      data: e => getToOrderQuantityForOrderItem(e, allItems),
    },
    supplierCompanyId: {
      key: 'supplierCompanyId',
      label: 'Supplier',
      size: 75,
      visible: false,
      style: rowStyle,
      data: e => getSupplierCompanyNameForOrderItem(e),
    },
    notes: {
      key: 'notes',
      label: 'Notes',
      size: 75,
      visible: false,
      style: rowStyle,
      data: e => get(e, 'notes'),
    },
    department: {
      key: 'department',
      label: 'Department',
      size: 75,
      visible: false,
      style: rowStyle,
      data: e => get(e, 'departmentName'),
    },
  }

  if (canViewCosts) {
    ret.unitCost = {
      key: 'unitCost',
      label: 'Unit Cost',
      size: 75,
      align: 'right',
      visible: true,
      style: rowStyle,
      data: e => <FinancialString
          value={get(e, 'unitCost')}
          precision={pricingAccuracy}/>,
      order: 5,
    }

    ret.totalCost = {
      key: 'totalCost',
      label: 'Total Cost',
      size: 80,
      align: 'right',
      visible: false,
      style: rowStyle,
      data: e => <FinancialString value={get(e, 'totalCost')}/>,
    }
  }

  userFields.forEach(field => {
    const def = FieldsHelper.getUserFieldPivotTableColumnDefinition(user, field)
    ret[def.key] = def
  })

  return ret
}

class QuoteItemPivotTable extends React.Component {
  onFilterDeleteItems = (orderItems) => {
    return QuoteHelper.filterDeletedOrderItems(orderItems)
  }

  render() {
    const {
      stateKey,
      onQuoteItemSelect,
      defaultVisibleColumns,
      defaultColumnSizes,
      ...otherProps
    } = this.props

    const columnsDefinition = getColumnsDefinition(this.props)
    const visibleColumns = applyColumnVisibility(columnsDefinition, defaultVisibleColumns)
    const sizedColumns = applyColumnSizes(visibleColumns, defaultColumnSizes)

    return (
        <PivotTable
            stateKey={stateKey}
            singularNoun={singular}
            pluralNoun={plural}
            mode={'grid'}
            icon={icon}
            columns={sizedColumns}
            refreshingAllowed={false}
            onSelectEntity={onQuoteItemSelect}
            onData={this.onFilterDeleteItems}
            showSubData
            {...otherProps}
            subDataProp={'subItems'}
            sortingAllowed={false}
        />
    )
  }
}

QuoteItemPivotTable.propTypes = {
  order: propTypes.object,
  stateKey: propTypes.string.isRequired,
  onUpdateFilters: propTypes.func,
  onQuoteItemSelect: propTypes.func,
  currencyId: propTypes.number,
  user: propTypes.object.isRequired,
  userFields: propTypes.array.isRequired,
  currencies: propTypes.array.isRequired,
  packTypes: propTypes.array.isRequired,
  nominalCodes: propTypes.array.isRequired,
  homeCurrencyUnicodePattern: propTypes.string.isRequired,
  pricingAccuracy: propTypes.number.isRequired,
  regionalLocaleTaxTerm: propTypes.string.isRequired,
  defaultVisibleColumns: propTypes.arrayOf(propTypes.string),
  defaultColumnSizes: propTypes.object,
  maxDescLength: propTypes.number,
  allItems: propTypes.array,
}

QuoteItemPivotTable.defaultProps = {
  userFields: [],
  currencies: [],
  packTypes: [],
  nominalCodes: [],
  defaultVisibleColumns: [
    'letter',
    'partCode',
    'desc',
    'quantity',
    'packType',
    'unitCost',
    'markup',
    'unitPrice',
    'discount',
    'netPrice',
  ],
  defaultColumnWidths: {},
}

const mapStateToProps = (state, ownProps) => {
  const data = getSelectedOrderItems(state)
  let {allItems} = ownProps

  if (isNil(allItems))
    allItems = getAllItems(data)

  return {
    user: getUser(state),
    userFields: state.data.quotationItemFields || [],
    currencies: getCurrencies(state),
    packTypes: getPackTypes(state),
    nominalCodes: getNominalCodes(state),
    homeCurrencyUnicodePattern: state.auth.user.currencyUnicodePattern || '',
    pricingAccuracy: getPricingAccuracy(state),
    regionalLocaleTaxTerm: getRegionalLocaleTaxTerm(state),
    allItems,
  }
}

export default connect(mapStateToProps)(QuoteItemPivotTable)
